import React, { useState } from 'react';
import { MessageSquareIcon, SparklesIcon } from 'lucide-react';
import { WidgetConfig } from '../../../data/widgetConfig';
import { readableTextOn } from '../../../utils/theme';
import { TalabMark as BrandMark } from '../../Logo';
export interface PanelProps {
  widget: WidgetConfig;
  update: (patch: Partial<WidgetConfig>) => void;
  /** Brand colours pulled from the merchant's shop theme. */
  brand: {
    primary: string;
    accent: string;
  };
  handle: string;
  canRemoveBranding: boolean;
  onUpgrade: () => void;
  /** Flips the preview to its closed state so the launcher is visible. */
  previewLauncher?: () => void;
}

/** A miniature of the launcher exactly as configured. */
export function LauncherSample({
  widget,
  launcher = widget.launcher,
  scale = 1
}: {
  widget: WidgetConfig;
  launcher?: WidgetConfig['launcher'];
  scale?: number;
}) {
  const onPrimary = readableTextOn(widget.primaryColor);
  const white = onPrimary === '#FFFFFF';
  const branded = widget.showTalabBranding;
  const label = widget.launcherLabel || 'Ask AI';
  const wordmark = <>
      <span className="w-px self-stretch my-0.5" style={{
      background: white ? 'rgba(255,255,255,0.28)' : 'rgba(27,35,51,0.28)'
    }} data-id="element-5037" data-mp="2t21n95zgncni" />
      <span className="font-display font-bold leading-none flex-shrink-0" style={{
      fontSize: 12,
      letterSpacing: '-0.035em',
      color: white ? '#FFFFFF' : '#1B2333',
      opacity: 0.88
    }} data-id="element-5038" data-mp="37pmo0t8v4zs9">
        talab
      </span>
    </>;
  let body: React.ReactNode;
  if (launcher === 'bubble') {
    body = <span className="relative w-11 h-11 rounded-full flex items-center justify-center shadow-sm" style={{
      background: widget.primaryColor,
      color: onPrimary
    }} data-id="element-5039" data-mp="2p0y7sklbrimt">
        <MessageSquareIcon className="w-5 h-5" data-id="element-5040" data-mp="4963ppram7qg" />
        {branded && <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center overflow-hidden" style={{
        border: `2px solid ${widget.primaryColor}`
      }} data-id="element-5041" data-mp="3vfz6gy1qiex3">
            <BrandMark size={12} tone="light" data-id="element-5042" data-mp="bc2yuyoskuwt" />
          </span>}
      </span>;
  } else {
    body = <span className="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs font-bold shadow-sm whitespace-nowrap" style={{
      background: widget.primaryColor,
      color: onPrimary,
      borderRadius: launcher === 'pill' ? 999 : '10px 10px 0 0'
    }} data-id="element-5043" data-mp="gd0bkvr18wqx">
        <SparklesIcon className="w-3.5 h-3.5" style={{
        color: widget.accentColor
      }} data-id="element-5044" data-mp="3osk3nrfyw94e" />
        {label}
        {branded && wordmark}
      </span>;
  }
  return scale === 1 ? <>{body}</> : <span className="inline-block origin-center" style={{
    transform: `scale(${scale})`
  }} data-id="element-5045" data-mp="2czelrdw29gq">
      {body}
    </span>;
}
const SWATCHES = ['#4C7FC1', '#1E3A5F', '#4F46E5', '#0F766E', '#B45309', '#BE123C', '#0B1220', '#F5CE3E'];

/** Accepts 3 or 6 digit hex, with or without the leading hash. */
function normaliseHex(input: string): string | null {
  const clean = input.trim().replace(/^#/, '');
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean)) return null;
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  return `#${full.toUpperCase()}`;
}

/**
 * Colour choice with an always-visible code field — the widget is often
 * matched to an existing website, where the exact code matters.
 */
export function ColourRow({
  label,
  value,
  onChange,
  hint
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  // A half-typed code never repaints the widget; it commits once valid.
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const shown = focused ? draft : value;
  const invalid = focused && draft.trim() !== '' && !normaliseHex(draft);
  const commit = (next: string) => {
    setDraft(next);
    const hex = normaliseHex(next);
    if (hex) onChange(hex);
  };
  return <div data-id="element-5046" data-mp="2lj2ccwms49m3">
      <p className="text-[13px] font-semibold text-dark-navy" data-id="element-5047" data-mp="2lisk12e9hqd8">{label}</p>
      {hint && <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-5048" data-mp="26u5xafmb1wbz">{hint}</p>}
      <div className="flex items-center gap-2 mt-2.5" data-id="element-5049" data-mp="26tmn5l2wcc8i">
        <label className="w-10 h-10 rounded-xl ring-1 ring-black/10 cursor-pointer flex-shrink-0 overflow-hidden" style={{
        background: value
      }} title="Pick a colour" data-id="element-5050" data-mp="2bsqwcbptm9yu">
          <input type="color" value={value} onChange={e => {
          setDraft(e.target.value.toUpperCase());
          onChange(e.target.value.toUpperCase());
        }} className="opacity-0 w-full h-full cursor-pointer" aria-label={`${label} picker`} data-id="element-5051" data-mp="22sufuxdl4hev" />
        </label>
        <div className={`flex-1 min-w-0 flex items-center gap-1 px-3 py-2 rounded-xl bg-white ring-1 transition-shadow duration-150 ease-out ${invalid ? 'ring-error/60' : focused ? 'ring-2 ring-primary-indigo/40' : 'ring-slate-200'}`} data-id="element-5052" data-mp="3i78bj40xm9sz">
          <span className="text-sm font-mono text-slate-400" data-id="element-5053" data-mp="16n0rlis567qo">#</span>
          <input value={shown.replace(/^#/, '')} onChange={e => commit(e.target.value)} onFocus={() => {
          setDraft(value);
          setFocused(true);
        }} onBlur={() => {
          setFocused(false);
          setDraft(value);
        }} spellCheck={false} maxLength={7} placeholder="4C7FC1" className="flex-1 min-w-0 bg-transparent text-sm font-mono uppercase text-dark-navy placeholder:text-slate-300 outline-none" aria-label={`${label} code`} data-id="element-5054" data-mp="1lalwni8kuh18" />
        </div>
      </div>
      {invalid && <p className="text-[11.5px] text-error mt-1.5" data-id="element-5055" data-mp="9w8lndyx9w2m">Enter a 6-digit code, like 4C7FC1.</p>}
      <div className="flex items-center gap-2 flex-wrap mt-2.5" data-id="element-5056" data-mp="4y6tm3jiqmf0">
        {SWATCHES.map(s => {
        const on = value.toLowerCase() === s.toLowerCase();
        return <button key={s} type="button" onClick={() => {
          setDraft(s);
          onChange(s);
        }} aria-label={s} aria-pressed={on} className={`w-6 h-6 rounded-full transition-transform duration-150 ease-out hover:scale-110 ${on ? 'ring-2 ring-offset-2 ring-dark-navy' : 'ring-1 ring-black/10'}`} style={{
          background: s
        }} data-id="element-5057" data-mp="29yzu84vzsown" />;
      })}
      </div>
    </div>;
}
export const widgetInputCls = 'w-full px-3.5 py-2.5 bg-white ring-1 ring-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:ring-2 focus:ring-primary-indigo/40 transition-shadow duration-150 ease-out';

/** Plain labelled text input in the studio style. */
export function TextInput({
  label,
  value,
  onChange,
  placeholder,
  multiline
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  return <label className="block" data-id="element-5058" data-mp="3gre3dxpx615n">
      <span className="block text-[13px] font-semibold text-dark-navy mb-2" data-id="element-5059" data-mp="3gu8n1n13zhy0">{label}</span>
      {multiline ? <textarea rows={3} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`${widgetInputCls} resize-none`} data-id="element-5060" data-mp="3buvzt16bs8uw" /> : <input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={widgetInputCls} data-id="element-5061" data-mp="3cr8lo8h8und" />}
    </label>;
}